import React, { useState, useEffect, useCallback } from 'react';
import { useGame } from '../context/GameContext';
import sanitizeHtml from '../utils/sanitizeHtml';
import './MessagesModal.css';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

const MessagesModal = ({ character, onClose, onUnreadUpdate, onCharacterUpdate, initialComposeTo }) => {
  const { socket } = useGame();
  const [view, setView] = useState(initialComposeTo ? 'compose' : 'inbox'); // inbox | sent | read | compose
  const [messages, setMessages] = useState([]);
  const [totalMessages, setTotalMessages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [statusMessage, setStatusMessage] = useState({ text: '', type: 'info' });

  // Compose fields
  const [composeTo, setComposeTo] = useState(initialComposeTo || '');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [sending, setSending] = useState(false);

  // Attachment fields
  const [attachType, setAttachType] = useState('none'); // none | gold | item
  const [attachGold, setAttachGold] = useState('');
  const [attachItemIndex, setAttachItemIndex] = useState('');
  const [attachItemQty, setAttachItemQty] = useState('1');
  const [myInventory, setMyInventory] = useState({ gold: 0, items: [] });
  const [claimingAttachment, setClaimingAttachment] = useState(false);

  const token = localStorage.getItem('token');

  const fetchMessages = useCallback(async (folder = view, pg = page) => {
    if (folder === 'compose' || folder === 'read') return;
    try {
      setLoading(true);
      const endpoint = folder === 'sent' ? 'sent' : 'inbox';
      const response = await fetch(`${API_BASE_URL}/api/messages/${endpoint}?page=${pg}&limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        setTotalMessages(data.total || 0);
      } else {
        setStatusMessage({ text: 'Failed to load messages', type: 'error' });
      }
    } catch (error) {
      setStatusMessage({ text: 'Error connecting to server', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [token, view, page]);

  // Fetch inventory for attachment picker
  const fetchMyInventory = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/messages/my-inventory`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyInventory({ gold: data.gold || 0, items: data.items || [] });
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  }, [token]);

  useEffect(() => {
    if (view === 'inbox' || view === 'sent') {
      fetchMessages(view, page);
      setSelectedIds(new Set());
    }
  }, [view, page, fetchMessages]);

  // Listen for new messages arriving while modal is open
  useEffect(() => {
    if (!socket) return;
    const handleNewMessage = () => {
      if (view === 'inbox') {
        fetchMessages('inbox', page);
      }
    };
    socket.on('new_message', handleNewMessage);
    return () => socket.off('new_message', handleNewMessage);
  }, [socket, view, page, fetchMessages]);

  // Escape key to close or go back
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        if (view === 'read') {
          setView('inbox');
          setSelectedMessage(null);
        } else if (view === 'compose') {
          setView('inbox');
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [view, onClose]);

  const handleReadMessage = async (msg) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/messages/${msg.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedMessage(data.message);
        setView('read');
        // If it was unread, update the parent's unread count
        if (!msg.read_at && onUnreadUpdate) {
          onUnreadUpdate();
        }
      }
    } catch (error) {
      setStatusMessage({ text: 'Error loading message', type: 'error' });
    }
  };

  const handleCompose = (replyTo = null) => {
    if (replyTo) {
      setComposeTo(replyTo.sender_name || '');
      setComposeSubject(
        (replyTo.subject || '').startsWith('Re: ')
          ? replyTo.subject
          : `Re: ${replyTo.subject || 'No Subject'}`
      );
      setComposeBody('');
    } else {
      setComposeTo('');
      setComposeSubject('');
      setComposeBody('');
    }
    // Reset attachment fields
    setAttachType('none');
    setAttachGold('');
    setAttachItemIndex('');
    setAttachItemQty('1');
    fetchMyInventory();
    setView('compose');
    setStatusMessage({ text: '', type: 'info' });
  };

  const handleSend = async () => {
    if (!composeTo.trim()) {
      setStatusMessage({ text: 'Please enter a recipient name', type: 'error' });
      return;
    }
    if (!composeBody.trim()) {
      setStatusMessage({ text: 'Please enter a message', type: 'error' });
      return;
    }
    if (composeBody.length > 500) {
      setStatusMessage({ text: 'Message must be 500 characters or fewer', type: 'error' });
      return;
    }

    // Validate attachment fields
    if (attachType === 'gold') {
      const amt = parseInt(attachGold, 10);
      if (!amt || amt < 1) {
        setStatusMessage({ text: 'Please enter a positive gold amount', type: 'error' });
        return;
      }
      if (amt > myInventory.gold) {
        setStatusMessage({ text: `You only have ${myInventory.gold} gold`, type: 'error' });
        return;
      }
    }
    if (attachType === 'item') {
      if (attachItemIndex === '' || attachItemIndex === undefined) {
        setStatusMessage({ text: 'Please select an item to attach', type: 'error' });
        return;
      }
      const qty = parseInt(attachItemQty, 10);
      if (!qty || qty < 1) {
        setStatusMessage({ text: 'Please enter a valid item quantity', type: 'error' });
        return;
      }
      const selectedItem = myInventory.items.find(i => i.index === parseInt(attachItemIndex, 10));
      if (selectedItem && qty > selectedItem.quantity) {
        setStatusMessage({ text: `You only have ${selectedItem.quantity} of ${selectedItem.name}`, type: 'error' });
        return;
      }
    }

    try {
      setSending(true);
      const payload = {
        recipientName: composeTo.trim(),
        subject: composeSubject.trim(),
        message: composeBody.trim()
      };

      if (attachType === 'gold') {
        payload.attachType = 'gold';
        payload.attachGold = parseInt(attachGold, 10);
      } else if (attachType === 'item') {
        payload.attachType = 'item';
        payload.attachItemIndex = parseInt(attachItemIndex, 10);
        payload.attachItemQty = parseInt(attachItemQty, 10) || 1;
      }

      const response = await fetch(`${API_BASE_URL}/api/messages/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();

      if (response.ok && data.success) {
        setStatusMessage({ text: data.message || 'Message sent!', type: 'success' });
        // Notify recipient via socket
        if (socket && data.recipientId) {
          socket.emit('notify_new_message', {
            recipientId: data.recipientId,
            senderName: character?.name || 'Unknown',
            subject: composeSubject.trim() || 'No Subject'
          });
        }
        // Refresh character data if gold/items were deducted
        if ((data.goldDeducted > 0 || data.itemDeducted) && onCharacterUpdate) {
          onCharacterUpdate();
        }
        // Return to inbox after short delay
        setTimeout(() => {
          setView('inbox');
          setPage(1);
        }, 1000);
      } else {
        setStatusMessage({ text: data.error || 'Failed to send message', type: 'error' });
      }
    } catch (error) {
      setStatusMessage({ text: 'Error sending message', type: 'error' });
    } finally {
      setSending(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/messages/delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ messageIds: Array.from(selectedIds) })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setStatusMessage({ text: data.message, type: 'success' });
        setSelectedIds(new Set());
        fetchMessages(view, page);
        if (onUnreadUpdate) onUnreadUpdate();
      } else {
        setStatusMessage({ text: data.error || 'Failed to delete', type: 'error' });
      }
    } catch (error) {
      setStatusMessage({ text: 'Error deleting messages', type: 'error' });
    }
  };

  const handleClaimAttachment = async (messageId) => {
    try {
      setClaimingAttachment(true);
      const response = await fetch(`${API_BASE_URL}/api/messages/claim-attachment/${messageId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setStatusMessage({ text: `${data.message}`, type: 'success' });
        // Update the selected message to show claimed state
        setSelectedMessage(prev => prev ? { ...prev, attachment_claimed: 1 } : prev);
        // Refresh character data (gold/inventory changed)
        if (onCharacterUpdate) onCharacterUpdate();
        // Refresh inbox to update attachment column
        fetchMessages('inbox', page);
      } else {
        setStatusMessage({ text: data.error || 'Failed to claim attachment', type: 'error' });
      }
    } catch (error) {
      setStatusMessage({ text: 'Error claiming attachment', type: 'error' });
    } finally {
      setClaimingAttachment(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === messages.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(messages.map(m => m.id)));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  };

  const totalPages = Math.max(1, Math.ceil(totalMessages / 20));

  const renderInbox = () => (
    <div className="messages-list-container">
      <div className="messages-toolbar">
        <button className="msg-btn msg-btn-primary" onClick={() => handleCompose()}>
          ✨ New Message
        </button>
        {view === 'inbox' && selectedIds.size > 0 && (
          <button className="msg-btn msg-btn-danger" onClick={handleDeleteSelected}>
            🗑️ Delete ({selectedIds.size})
          </button>
        )}
        <button className="msg-btn msg-btn-refresh" onClick={() => fetchMessages(view, page)}>
          🔄 Refresh
        </button>
      </div>

      {loading ? (
        <div className="messages-loading">Loading messages...</div>
      ) : messages.length === 0 ? (
        <div className="messages-empty">
          {view === 'inbox' ? 'Your inbox is empty.' : 'You have not sent any messages.'}
        </div>
      ) : (
        <>
          <div className="messages-table">
            <div className="msg-header-row">
              {view === 'inbox' && (
                <div className="msg-col-check">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === messages.length && messages.length > 0}
                    onChange={toggleSelectAll}
                  />
                </div>
              )}
              <div className="msg-col-from">{view === 'inbox' ? 'From' : 'To'}</div>
              <div className="msg-col-subject">Subject</div>
              <div className="msg-col-attach"></div>
              <div className="msg-col-date">Date</div>
            </div>

            {messages.map(msg => {
              const isUnread = view === 'inbox' && !msg.read_at;
              const hasAttachment = (msg.attached_gold > 0 || (msg.attached_item_name && msg.attached_item_qty > 0));
              const isClaimed = msg.attachment_claimed === 1 || msg.attachment_claimed === true;
              return (
                <div
                  key={msg.id}
                  className={`msg-row ${isUnread ? 'msg-unread' : ''} ${selectedIds.has(msg.id) ? 'msg-selected' : ''}`}
                >
                  {view === 'inbox' && (
                    <div className="msg-col-check">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(msg.id)}
                        onChange={() => toggleSelect(msg.id)}
                      />
                    </div>
                  )}
                  <div className="msg-col-from" onClick={() => handleReadMessage(msg)}>
                    {view === 'inbox' ? msg.sender_name : msg.recipient_name}
                  </div>
                  <div className="msg-col-subject" onClick={() => handleReadMessage(msg)}>
                    {isUnread && <span className="msg-unread-dot"></span>}
                    {msg.subject || 'No Subject'}
                  </div>
                  <div className="msg-col-attach" onClick={() => handleReadMessage(msg)}>
                    {hasAttachment ? (isClaimed ? '' : '') : ''}
                  </div>
                  <div className="msg-col-date" onClick={() => handleReadMessage(msg)}>
                    {formatDate(msg.created_at)}
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="messages-pagination">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
              <span>Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next </button>
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderReadMessage = () => {
    if (!selectedMessage) return null;
    const hasGold = selectedMessage.attached_gold > 0;
    const hasItem = selectedMessage.attached_item_name && selectedMessage.attached_item_qty > 0;
    const hasAttachment = hasGold || hasItem;
    const isClaimed = selectedMessage.attachment_claimed === 1 || selectedMessage.attachment_claimed === true;
    const isRecipient = selectedMessage.recipient_id === character?.id;

    return (
      <div className="message-read-container">
        <div className="message-read-header">
          <div className="message-read-field">
            <span className="field-label">From:</span>
            <span className="field-value">{selectedMessage.sender_name}</span>
          </div>
          <div className="message-read-field">
            <span className="field-label">To:</span>
            <span className="field-value">{selectedMessage.recipient_name}</span>
          </div>
          <div className="message-read-field">
            <span className="field-label">Subject:</span>
            <span className="field-value">{selectedMessage.subject || 'No Subject'}</span>
          </div>
          <div className="message-read-field">
            <span className="field-label">Date:</span>
            <span className="field-value">
              {new Date(selectedMessage.created_at).toLocaleString()}
            </span>
          </div>
        </div>
        <div
          className="message-read-body"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(
            (selectedMessage.message || '').replace(/\n/g, '<br>')
          )}}
        />

        {hasAttachment && (
          <div className={`message-attachment-box ${isClaimed ? 'claimed' : ''}`}>
            <div className="attachment-header">Attachment</div>
            <div className="attachment-contents">
              {hasGold && (
                <div className="attachment-item">
                  <span className="attachment-icon"></span>
                  <span>{selectedMessage.attached_gold.toLocaleString()} gold</span>
                </div>
              )}
              {hasItem && (
                <div className="attachment-item">
                  <span className="attachment-icon"></span>
                  <span>{selectedMessage.attached_item_qty}x {selectedMessage.attached_item_name}</span>
                </div>
              )}
            </div>
            {isRecipient && !isClaimed && (
              <button
                className="msg-btn msg-btn-claim"
                onClick={() => handleClaimAttachment(selectedMessage.id)}
                disabled={claimingAttachment}
              >
                {claimingAttachment ? 'Claiming...' : '✨ Claim Attachment'}
              </button>
            )}
            {isClaimed && (
              <div className="attachment-claimed-label">Already claimed</div>
            )}
          </div>
        )}

        <div className="message-read-actions">
          <button
            className="msg-btn msg-btn-primary"
            onClick={() => handleCompose(selectedMessage)}
          >
             Reply
          </button>
          <button
            className="msg-btn"
            onClick={() => { setView('inbox'); setSelectedMessage(null); }}
          >
            ⬅️ Back to Inbox
          </button>
        </div>
      </div>
    );
  };

  const renderCompose = () => (
    <div className="message-compose-container">
      <div className="compose-field">
        <label>To:</label>
        <input
          type="text"
          value={composeTo}
          onChange={e => setComposeTo(e.target.value)}
          placeholder="Character name"
          maxLength={50}
          autoFocus
        />
      </div>
      <div className="compose-field">
        <label>Subject:</label>
        <input
          type="text"
          value={composeSubject}
          onChange={e => setComposeSubject(e.target.value)}
          placeholder="Subject (optional)"
          maxLength={200}
        />
      </div>
      <div className="compose-field compose-body-field">
        <label>Message:</label>
        <textarea
          value={composeBody}
          onChange={e => setComposeBody(e.target.value)}
          placeholder="Write your message..."
          maxLength={500}
          rows={6}
        />
        <div className="compose-char-count">
          {composeBody.length}/500
        </div>
      </div>

      <div className="compose-attachment-section">
        <div className="compose-attachment-label">Attach:</div>
        <div className="compose-attachment-options">
          <label className="attach-option">
            <input
              type="radio"
              name="attachType"
              value="none"
              checked={attachType === 'none'}
              onChange={() => setAttachType('none')}
            />
            <span>Nothing</span>
          </label>
          <label className="attach-option">
            <input
              type="radio"
              name="attachType"
              value="gold"
              checked={attachType === 'gold'}
              onChange={() => setAttachType('gold')}
            />
            <span>💰 Gold</span>
          </label>
          <label className="attach-option">
            <input
              type="radio"
              name="attachType"
              value="item"
              checked={attachType === 'item'}
              onChange={() => setAttachType('item')}
            />
            <span>Item</span>
          </label>
        </div>

        {attachType === 'gold' && (
          <div className="compose-attach-detail">
            <div className="attach-detail-row">
              <label>Amount:</label>
              <input
                type="number"
                value={attachGold}
                onChange={e => setAttachGold(e.target.value)}
                placeholder="Gold amount"
                min="1"
                max={myInventory.gold}
              />
              <span className="attach-hint">You have {myInventory.gold.toLocaleString()} gp</span>
            </div>
          </div>
        )}

        {attachType === 'item' && (() => {
          const selectedItem = attachItemIndex !== '' ? myInventory.items.find(i => i.index === parseInt(attachItemIndex, 10)) : null;
          const maxQty = selectedItem?.quantity || 0;
          const parsedQty = parseInt(attachItemQty, 10);
          const qtyExceeded = selectedItem && parsedQty > maxQty;
          const qtyInvalid = selectedItem && (!parsedQty || parsedQty < 1);
          return (
          <div className="compose-attach-detail">
            <div className="attach-detail-row">
              <label>Item:</label>
              <select
                value={attachItemIndex}
                onChange={e => {
                  setAttachItemIndex(e.target.value);
                  setAttachItemQty('1');
                }}
              >
                <option value="">-- Select Item --</option>
                {myInventory.items.map(item => (
                  <option key={item.index} value={item.index}>
                    {item.name} ({item.quantity})
                  </option>
                ))}
              </select>
            </div>
            {attachItemIndex !== '' && (
              <div className="attach-detail-row">
                <label>Qty:</label>
                <input
                  type="number"
                  className={qtyExceeded || qtyInvalid ? 'attach-qty-error' : ''}
                  value={attachItemQty}
                  onChange={e => setAttachItemQty(e.target.value)}
                  min="1"
                  max={maxQty}
                />
                <span className={`attach-hint${qtyExceeded ? ' attach-hint-error' : ''}`}>
                  {qtyExceeded ? `Too many! You only have ${maxQty}` : `Max: ${maxQty}`}
                </span>
              </div>
            )}
          </div>
          );
        })()}
      </div>

      <div className="compose-actions">
        <button
          className="msg-btn msg-btn-primary"
          onClick={handleSend}
          disabled={sending || (attachType === 'item' && attachItemIndex !== '' && (() => {
            const sel = myInventory.items.find(i => i.index === parseInt(attachItemIndex, 10));
            const q = parseInt(attachItemQty, 10);
            return !sel || !q || q < 1 || q > (sel.quantity || 0);
          })()) || (attachType === 'gold' && parseInt(attachGold, 10) > myInventory.gold)}
        >
          {sending ? 'Sending...' : '📨 Send Message'}
        </button>
        <button
          className="msg-btn"
          onClick={() => setView('inbox')}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="messages-overlay" onClick={onClose}>
      <div className="messages-modal" onClick={e => e.stopPropagation()}>
        <div className="messages-header">
          <h2>✉️ Messages</h2>
          <button className="messages-close" onClick={onClose}>✖</button>
        </div>

        {view !== 'read' && view !== 'compose' && (
          <div className="messages-tabs">
            <button
              className={`messages-tab ${view === 'inbox' ? 'active' : ''}`}
              onClick={() => { setView('inbox'); setPage(1); }}
            >
               Inbox
            </button>
            <button
              className={`messages-tab ${view === 'sent' ? 'active' : ''}`}
              onClick={() => { setView('sent'); setPage(1); }}
            >
               Sent
            </button>
          </div>
        )}

        <div className="messages-content">
          {(view === 'inbox' || view === 'sent') && renderInbox()}
          {view === 'read' && renderReadMessage()}
          {view === 'compose' && renderCompose()}
        </div>

        {statusMessage.text && (
          <div className={`messages-status ${statusMessage.type}`}>
            {statusMessage.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesModal;
